/**
 * Applies annotation mark character ranges to a ProseMirror document.
 *
 * This is the inverse of `extractAnnotationRanges`. Given:
 *   - A PM document parsed from clean markdown (no annotation syntax)
 *   - The markdown string that was parsed
 *   - An array of { id, from, to } annotation ranges (character offsets into markdown)
 *
 * It returns a new PM document where each text node overlapping with an
 * annotation range carries the corresponding annotation mark.
 *
 * This is used on commit in the markdown source view: the user has edited
 * clean markdown, CM6 has tracked the annotation ranges through their edits
 * (via DecorationSet.map()), and we now need to write the PM document back
 * with annotation marks re-applied at the (possibly shifted) positions.
 *
 * Overlap handling
 * ----------------
 * Multiple annotation marks with different IDs can overlap (the same text can
 * carry two different inline comments). ProseMirror's `tr.addMark()` removes
 * any existing mark of the same type before adding a new one — which would
 * cause later annotations to erase earlier ones in the overlap region.
 *
 * To handle this correctly, we build the document by reconstructing text nodes
 * directly: we compute all the markdown character boundaries where annotation
 * membership changes (sorted unique boundaries of all ranges), then create one
 * text node per sub-span with the correct set of annotation marks.
 */

import type { Node as PMNode, Mark as PMMark } from '@atlaskit/editor-prosemirror/model';
import type { MarkdownSourceAnnotationRange } from './types';

/**
 * Escape a raw PM text string the same way `mdast-util-to-markdown` does, so
 * we can search for it in the serialized markdown when a direct `indexOf` fails.
 *
 * Mirrors the identical helper in `extractAnnotationRanges.ts` — kept local
 * here to avoid a circular dependency between the two files.
 */
function escapedForMarkdown(text: string): string {
	return text.replace(/[*_`[\\\]]/gu, (ch) => `\\${ch}`);
}

/**
 * Apply annotation mark ranges to a PM document and return the new document.
 *
 * @param doc - A PM document parsed from clean markdown (no annotation syntax).
 * @param markdown - The markdown string that was parsed to produce `doc`.
 *   Used to verify that annotation ranges are within bounds.
 * @param annotations - Annotation ranges with character offsets into `markdown`.
 * @returns A new PM document with annotation marks applied at the given ranges.
 */
export function applyAnnotationRanges(
	doc: PMNode,
	markdown: string,
	annotations: MarkdownSourceAnnotationRange[],
): PMNode {
	if (annotations.length === 0) {
		return doc;
	}

	const schema = doc.type.schema;
	const annotationMarkType = schema.marks.annotation;

	// If the schema doesn't have an annotation mark, return unchanged.
	if (!annotationMarkType) {
		return doc;
	}

	// Filter to valid, in-bounds annotations only.
	const valid = annotations.filter((a) => a.from >= 0 && a.to <= markdown.length && a.from < a.to);

	if (valid.length === 0) {
		return doc;
	}

	/**
	 * Given a markdown character range [mdFrom, mdTo), return the set of
	 * annotation marks that should be applied to it.
	 */
	function marksForMdRange(mdFrom: number, mdTo: number): PMMark[] {
		const marks: PMMark[] = [];
		for (const ann of valid) {
			if (ann.from < mdTo && ann.to > mdFrom) {
				marks.push(annotationMarkType.create({ id: ann.id, annotationType: 'inlineComment' }));
			}
		}
		return marks;
	}

	/**
	 * Split a text node's content into sub-spans at annotation boundaries, and
	 * create new PM text nodes with the correct marks for each sub-span.
	 *
	 * @param text - The text content of the node.
	 * @param existingMarks - Any non-annotation marks already on the node.
	 * @param mdFrom - The markdown character offset of the start of this text.
	 * @returns An array of PM text nodes (may be a single node if no annotations
	 *   overlap this text, or multiple if the annotation boundaries split it).
	 *
	 * Splitting is always performed regardless of whether the text node already
	 * carries formatting marks (em, strong, code, etc.). This is safe because
	 * convertInlineChildren in mdast-util-from-pm merges adjacent text nodes with
	 * identical non-annotation marks before serialization — e.g.
	 * ["b"(strong), "old"(strong+ann)] serializes as "**bold**", not "**b****old**".
	 */
	function splitTextNode(text: string, existingMarks: readonly PMMark[], mdFrom: number): PMNode[] {
		const mdTo = mdFrom + text.length;

		// Split at annotation boundaries.
		const boundaries = new Set<number>();
		boundaries.add(mdFrom);
		boundaries.add(mdTo);
		for (const ann of valid) {
			if (ann.from > mdFrom && ann.from < mdTo) boundaries.add(ann.from);
			if (ann.to > mdFrom && ann.to < mdTo) boundaries.add(ann.to);
		}

		const sortedBoundaries = [...boundaries].sort((a, b) => a - b);

		// If there are only two boundaries (start and end), no splits needed.
		// Just apply the correct marks to the whole node.
		if (sortedBoundaries.length === 2) {
			const annMarks = marksForMdRange(mdFrom, mdTo);
			const allMarks = schema.marks.annotation
				? [...existingMarks.filter((m) => m.type.name !== 'annotation'), ...annMarks]
				: existingMarks;
			return [schema.text(text, allMarks)];
		}

		// Multiple sub-spans — create one text node per sub-span.
		const nodes: PMNode[] = [];
		for (let i = 0; i < sortedBoundaries.length - 1; i++) {
			const spanMdFrom = sortedBoundaries[i];
			const spanMdTo = sortedBoundaries[i + 1];
			const spanText = text.slice(spanMdFrom - mdFrom, spanMdTo - mdFrom);
			if (!spanText) continue;

			const annMarks = marksForMdRange(spanMdFrom, spanMdTo);
			const allMarks = [...existingMarks.filter((m) => m.type.name !== 'annotation'), ...annMarks];
			nodes.push(schema.text(spanText, allMarks));
		}
		return nodes;
	}

	// Walk the PM document and rebuild it with annotation marks applied.
	// We track the markdown offset as we encounter text nodes (same
	// forward-search strategy as extractAnnotationRanges).
	let searchFrom = 0;

	function rebuildChildren(parent: PMNode): PMNode[] {
		const result: PMNode[] = [];
		parent.forEach((child) => {
			if (child.isText && child.text) {
				const text = child.text;
				let mdIdx = markdown.indexOf(text, searchFrom);
				let matchLength = text.length;
				if (mdIdx === -1) {
					// The serializer may have escaped special characters (e.g. `*` → `\*`),
					// making the raw PM text no longer a direct substring. Retry with the
					// escaped form as a fallback — mirrors the same logic in extractAnnotationRanges.
					const escaped = escapedForMarkdown(text);
					if (escaped !== text) {
						mdIdx = markdown.indexOf(escaped, searchFrom);
						if (mdIdx !== -1) {
							matchLength = escaped.length;
						}
					}
				}
				if (mdIdx === -1) {
					// Still not found — skip without advancing searchFrom, same as
					// extractAnnotationRanges does for untranslatable nodes.
					result.push(child);
					return;
				}
				const nodes = splitTextNode(text, child.marks, mdIdx);
				searchFrom = mdIdx + matchLength;
				result.push(...nodes);
			} else if (child.isLeaf) {
				result.push(child);
			} else {
				const newChildren = rebuildChildren(child);
				try {
					result.push(
						schema.nodes[child.type.name].createChecked(child.attrs, newChildren, child.marks),
					);
				} catch {
					// If createChecked fails (e.g. content constraint violation), fall back.
					result.push(child);
				}
			}
		});
		return result;
	}

	// Rebuild top-level (doc) children.
	const topChildren = rebuildChildren(doc);
	return schema.nodes[doc.type.name].createChecked(doc.attrs, topChildren, doc.marks);
}
